/**
 * iPhone mockup with full-bleed Mux (or progressive video) — ported from
 * creadoresdecontenido PhoneVideoMockup, re-skinned for formal black media kit.
 */
import MuxPlayer from "@mux/mux-player-react";
import type MuxPlayerElement from "@mux/mux-player";
import { Volume2, VolumeX } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type MouseEvent,
  type VideoHTMLAttributes,
} from "react";
import "./PhoneVideoMockup.css";

export type PhoneVideoMockupProps = {
  /** Mux public playback id (preferred). */
  playbackId?: string;
  /** Progressive URL fallback (mp4/webm) if no playbackId. */
  src?: string;
  /** Frame while loading (Cloudinary or Mux thumbnail). */
  poster?: string;
  className?: string;
  ariaLabel?: string;
  videoProps?: Omit<
    VideoHTMLAttributes<HTMLVideoElement>,
    "src" | "poster" | "className"
  >;
} & Omit<HTMLAttributes<HTMLDivElement>, "className">;

/**
 * Device frame + muted autoplay loop. Mute control is the only interactive chrome.
 */
export function PhoneVideoMockup({
  playbackId,
  src,
  poster,
  className = "",
  ariaLabel = "Vista previa de video en dispositivo móvil",
  videoProps,
  ...containerProps
}: PhoneVideoMockupProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const muxRef = useRef<MuxPlayerElement | null>(null);
  const [muted, setMuted] = useState(true);

  const muxId = playbackId?.trim() ?? "";
  const videoUrl = src?.trim() ?? "";
  const hasMux = Boolean(muxId);
  const hasVideo = hasMux || Boolean(videoUrl);

  // Progressive <video>: reliable autoplay + reduced-motion
  useEffect(() => {
    if (hasMux) return;
    const el = videoRef.current;
    if (!el || !hasVideo) return;

    const preferReduce = window.matchMedia("(prefers-reduced-motion: reduce)");

    const tryPlay = () => {
      if (preferReduce.matches) {
        el.pause();
        return;
      }
      el.muted = muted;
      void el.play().catch(() => {
        /* autoplay blocked — poster / static frame */
      });
    };

    tryPlay();
    preferReduce.addEventListener("change", tryPlay);
    el.addEventListener("loadeddata", tryPlay);
    return () => {
      preferReduce.removeEventListener("change", tryPlay);
      el.removeEventListener("loadeddata", tryPlay);
    };
  }, [hasMux, hasVideo, videoUrl, muted]);

  // Mux: pause when reduced-motion
  useEffect(() => {
    if (!hasMux) return;
    const preferReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      const player = muxRef.current;
      if (!player) return;
      if (preferReduce.matches) {
        void Promise.resolve(player.pause()).catch(() => {});
      }
    };
    apply();
    preferReduce.addEventListener("change", apply);
    return () => preferReduce.removeEventListener("change", apply);
  }, [hasMux, muxId]);

  const toggleMute = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const next = !muted;
      setMuted(next);

      if (hasMux) {
        const player = muxRef.current;
        if (player) {
          player.muted = next;
          if (!next) {
            player.volume = 1;
            void Promise.resolve(player.play()).catch(() => {});
          }
        }
      } else {
        const el = videoRef.current;
        if (el) {
          el.muted = next;
          if (!next) {
            el.volume = 1;
            void el.play().catch(() => {});
          }
        }
      }
    },
    [muted, hasMux],
  );

  return (
    <div className={`phone-mockup ${className}`.trim()} {...containerProps}>
      <div className="phone-mockup__frame">
        <div className="phone-mockup__screen">
          {hasMux ? (
            <MuxPlayer
              ref={muxRef}
              className="phone-mockup__video phone-mockup__mux"
              playbackId={muxId}
              poster={poster}
              streamType="on-demand"
              autoPlay="muted"
              muted={muted}
              loop
              playsInline
              preload="auto"
              accentColor="#c2ae7a"
              metadata={{
                video_title: "Media kit — phone preview",
              }}
              aria-label={ariaLabel}
            />
          ) : hasVideo ? (
            <video
              ref={videoRef}
              className="phone-mockup__video"
              src={videoUrl}
              poster={poster}
              playsInline
              muted={muted}
              loop
              autoPlay
              preload="auto"
              controls={false}
              aria-label={ariaLabel}
              {...videoProps}
            >
              Tu navegador no soporta la reproducción de video.
            </video>
          ) : (
            <div className="phone-mockup__placeholder" aria-hidden="true">
              <span className="phone-mockup__placeholder-label">
                Configurá un video Mux en el admin
              </span>
            </div>
          )}

          <div className="phone-mockup__dynamic-island" aria-hidden="true">
            <span className="phone-mockup__camera" />
          </div>

          {hasVideo ? (
            <button
              type="button"
              className="phone-mockup__sound"
              onClick={toggleMute}
              aria-pressed={!muted}
              aria-label={muted ? "Activar sonido" : "Silenciar video"}
              title={muted ? "Activar sonido" : "Silenciar"}
            >
              {muted ? (
                <VolumeX className="phone-mockup__sound-icon" aria-hidden />
              ) : (
                <Volume2 className="phone-mockup__sound-icon" aria-hidden />
              )}
            </button>
          ) : null}

          <div className="phone-mockup__home-indicator" aria-hidden="true" />
        </div>

        <span
          className="phone-mockup__button phone-mockup__button--action"
          aria-hidden="true"
        />
        <span
          className="phone-mockup__button phone-mockup__button--volume-up"
          aria-hidden="true"
        />
        <span
          className="phone-mockup__button phone-mockup__button--volume-down"
          aria-hidden="true"
        />
        <span
          className="phone-mockup__button phone-mockup__button--power"
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

export default PhoneVideoMockup;
